import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Box, Flex, Text, TextProps } from 'rebass'
import { useNewTranslation } from 'hooks/index'
import _ from 'lodash'

import {
    makeCustomSubmissionContentSelector,
    selectReviewPendingSubmission,
    makeSubmissionContentSelector,
    setMediaData,
    setMediaModalOpen,
    setSelectedMediaIndex,
    SubmissionProgressProps,
} from 'state/review'
import { State } from 'state'
import { theme } from 'theme'
import MediaViewer, { MediaData } from 'components/MediaViewer'
import { StepState } from 'state/tasks'
import styled from 'particles/themes/styled'

const MediaBtnWrapper: React.FC<any> = ({
    submissionContent,
    mt = theme.spacing.xxs,
    photoPadding = theme.spacing.xxs,
}) => {
    const dispatch = useDispatch()

    const isVerifyMode = useSelector(selectReviewPendingSubmission)

    const setMedia = useCallback((data: MediaData[]) => dispatch(setMediaData(data)), [])
    const setMediaModal = useCallback((isOpen: boolean) => dispatch(setMediaModalOpen(isOpen)), [])
    const setMediaIndex = useCallback((index: number) => dispatch(setSelectedMediaIndex(index)), [])
    return (
        <Flex>
            <Box p={photoPadding} mx="auto" mt={mt}>
                <MediaViewer
                    mediaData={submissionContent as MediaData}
                    showMediaIndex={0}
                    showPopup={false}
                    setModalOpen={setMediaModal}
                    setMediaData={setMedia}
                    setSelectedMediaIndex={setMediaIndex}
                    {...(!isVerifyMode && { maxHeight: '200px' })}
                />
            </Box>
        </Flex>
    )
}

interface CaptionTextProps extends TextProps {
    isVerifyMode: boolean
}

const CaptionText = styled(Text)<CaptionTextProps>`
    display: -webkit-box;
    ${(props: CaptionTextProps) =>
        props.isVerifyMode
            ? {
                  fontSize: '0.8em',
              }
            : {
                  WebkitLineClamp: 11,
              }}
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
`

interface SubmissionContentProps {
    submissionProgress: SubmissionProgressProps
    step: StepState
    showCaption?: boolean
    mt?: string
    photoPadding?: string
    showCustomStepTitle?: boolean
}

const CustomSubmissionContent: React.FC<any> = ({
    blockData,
    submissionProgress,
    mt = theme.spacing.xxs,
    photoPadding = theme.spacing.xxs,
    showCustomStepTitle = true,
}) => {
    const selectSubmissionContents = useMemo(makeCustomSubmissionContentSelector, [])
    const submissionData = useSelector((state: State) =>
        selectSubmissionContents(state, {
            submissionProgress,
            block: blockData,
        }),
    )
    const isVerifyMode = useSelector(selectReviewPendingSubmission)

    if (!blockData) {
        return null
    }

    let SubmissionComponent
    if (blockData.type === 'photo_submission') {
        SubmissionComponent = submissionData ? (
            <MediaBtnWrapper
                submissionContent={submissionData}
                submissionProgress={submissionProgress}
                isCustomStep={true}
                mt={mt}
                photoPadding={photoPadding}
            />
        ) : null
    } else if (blockData.type === 'text_submission') {
        SubmissionComponent = (
            <CaptionText isVerifyMode={!_.isEmpty(isVerifyMode)} my={theme.spacing.xxs}>
                {submissionData}
            </CaptionText>
        )
    } else if (blockData.type === 'url_submission') {
        SubmissionComponent = (
            <Box my={theme.spacing.xxs}>
                <a style={{ wordBreak: 'break-all' }} href={submissionData} target="_blank" rel="noopener noreferrer">
                    {submissionData}
                </a>
            </Box>
        )
    } else {
        return null
    }
    return (
        <>
            {showCustomStepTitle && (
                <Text fontSize="0.8em" fontWeight="bold">
                    {blockData.title}
                </Text>
            )}
            {SubmissionComponent}
        </>
    )
}

const PredefinedSubmissionContent: React.FC<SubmissionContentProps> = ({
    submissionProgress,
    step,
    showCaption = true,
    mt = theme.spacing.xxs,
    photoPadding = theme.spacing.xxs,
}) => {
    const [newT] = useNewTranslation()
    const isVerifyMode = useSelector(selectReviewPendingSubmission)
    const selectSubmissionContents = useMemo(makeSubmissionContentSelector, [])
    const { submissionContent, captionData, url, engagementData, video_code } = useSelector((state: State) =>
        selectSubmissionContents(state, {
            submissionProgress,
            step,
        }),
    )
    return (
        <Box>
            {url && (
                <>
                    <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                        {newT('submission.table.urlLabel')}
                    </CaptionText>
                    <Box sx={{ textAlign: 'left' }} my={theme.spacing.xxs}>
                        <a
                            style={{
                                wordBreak: 'break-all',
                            }}
                            href={url}
                        >
                            {url}
                        </a>
                    </Box>
                </>
            )}
            {video_code && (
                <>
                    <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                        {newT('submission.table.videoCodeLabel')}
                    </CaptionText>
                    <Box sx={{ textAlign: 'left' }} my={theme.spacing.xxs}>
                        <a
                            style={{
                                wordBreak: 'break-all',
                            }}
                            href={video_code}
                        >
                            {video_code}
                        </a>
                    </Box>
                </>
            )}
            {submissionContent && (
                <MediaBtnWrapper
                    submissionContent={submissionContent}
                    submissionProgress={submissionProgress}
                    mt={mt}
                    photoPadding={photoPadding}
                />
            )}
            {captionData && showCaption && (
                <>
                    {captionData.split('\n').map((captionParagraph: string, index: number) => (
                        <CaptionText
                            isVerifyMode={!_.isEmpty(isVerifyMode)}
                            key={'caption' + index}
                            my={theme.spacing.xxs}
                            textAlign="left"
                        >
                            {captionParagraph}
                        </CaptionText>
                    ))}
                </>
            )}
            {engagementData && (
                <>
                    {engagementData.view_count_screenshot_files && (
                        <MediaBtnWrapper
                            submissionContent={engagementData.view_count_screenshot_files}
                            submissionProgress={submissionProgress}
                            mt={mt}
                            photoPadding={photoPadding}
                        />
                    )}
                    {engagementData.insights_screenshot_files && (
                        <MediaBtnWrapper
                            submissionContent={engagementData.insights_screenshot_files}
                            submissionProgress={submissionProgress}
                            mt={mt}
                            photoPadding={photoPadding}
                        />
                    )}
                    {engagementData.follower_count && (
                        <>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {newT('submission.table.followerCountLabel')}
                            </CaptionText>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {engagementData.follower_count}
                            </CaptionText>
                        </>
                    )}
                    {engagementData.engagement_like_count && (
                        <>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {newT('engagementMetric.like')}
                            </CaptionText>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {engagementData.engagement_like_count}
                            </CaptionText>
                        </>
                    )}
                    {engagementData.engagement_reply_count && (
                        <>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {newT('engagementMetric.reply')}
                            </CaptionText>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {engagementData.engagement_reply_count}
                            </CaptionText>
                        </>
                    )}
                    {engagementData.engagement_retweet_count && (
                        <>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {newT('engagementMetric.retweet')}
                            </CaptionText>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {engagementData.engagement_retweet_count}
                            </CaptionText>
                        </>
                    )}
                    {engagementData.engagement_engagement_count && (
                        <>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {newT('engagementMetric.engagement')}
                            </CaptionText>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {engagementData.engagement_engagement_count}
                            </CaptionText>
                        </>
                    )}
                    {engagementData.engagement_comment_count && (
                        <>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {newT('engagementMetric.numberComments')}
                            </CaptionText>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {engagementData.engagement_comment_count}
                            </CaptionText>
                        </>
                    )}
                    {engagementData.engagement_view_count && (
                        <>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {newT('engagementMetric.view')}
                            </CaptionText>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {engagementData.engagement_view_count}
                            </CaptionText>
                        </>
                    )}
                    {engagementData.engagement_share_count && (
                        <>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {newT('engagementMetric.share')}
                            </CaptionText>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {engagementData.engagement_share_count}
                            </CaptionText>
                        </>
                    )}
                    {engagementData.engagement_tag_count && (
                        <>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {newT('engagementMetric.tag')}
                            </CaptionText>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {engagementData.engagement_tag_count}
                            </CaptionText>
                        </>
                    )}
                    {engagementData.engagement_sticker_tap_count && (
                        <>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {newT('engagementMetric.stickerTap')}
                            </CaptionText>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {engagementData.engagement_sticker_tap_count}
                            </CaptionText>
                        </>
                    )}
                    {engagementData.engagement_reach_count && (
                        <>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {newT('engagementMetric.reach')}
                            </CaptionText>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {engagementData.engagement_reach_count}
                            </CaptionText>
                        </>
                    )}
                    {engagementData.engagement_impression_count && (
                        <>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {newT('engagementMetric.impression')}
                            </CaptionText>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {engagementData.engagement_impression_count}
                            </CaptionText>
                        </>
                    )}
                    {engagementData.engagement_cta_click_count && (
                        <>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {newT(
                                    'engagementMetric.ctaButtonClicksBoostedPosts',
                                    'Number of CTA Button Clicks (for boosted posts)',
                                )}
                            </CaptionText>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {engagementData.engagement_cta_click_count}
                            </CaptionText>
                        </>
                    )}
                    {engagementData.engagement_save_count && (
                        <>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {newT('engagementMetric.save')}
                            </CaptionText>
                            <CaptionText isVerifyMode={false} my={theme.spacing.xxs} textAlign="left">
                                {engagementData.engagement_save_count}
                            </CaptionText>
                        </>
                    )}
                </>
            )}
        </Box>
    )
}

const SubmissionContent: React.FC<SubmissionContentProps> = ({
    submissionProgress,
    step,
    showCaption = true,
    mt = theme.spacing.xxs,
    photoPadding = theme.spacing.xxs,
    showCustomStepTitle = true,
}) => {
    return step.type === 'custom' ? (
        <Box mt={mt} sx={{ textAlign: 'left' }}>
            {step.data.blocks.map((blockData: any, index: number) => {
                return (
                    <CustomSubmissionContent
                        key={index}
                        blockData={blockData}
                        submissionProgress={submissionProgress}
                        mt={mt}
                        photoPadding={photoPadding}
                        showCustomStepTitle={showCustomStepTitle}
                    />
                )
            })}
        </Box>
    ) : (
        <PredefinedSubmissionContent
            submissionProgress={submissionProgress}
            step={step}
            showCaption={showCaption}
            mt={mt}
            photoPadding={photoPadding}
        />
    )
}

export default React.memo(SubmissionContent)
